import {
  CustomerSort,
  CustomerSortField,
  FinanceHubCustomerRow,
} from '@finance-hub/domain/customers/blocs/types/customer-list';

export const DEFAULT_CUSTOMER_SORT: CustomerSort = {
  direction: 'asc',
  field: 'customerName',
};

/**
 * Returns the sort selection after a column header is pressed.
 *
 * Pressing the active column toggles its direction; pressing any other column
 * starts it ascending.
 *
 * @param current - The active sort selection
 * @param field - The column header that was pressed
 *
 * @returns The next sort selection
 */
export const getNextCustomerSort = (
  current: CustomerSort,
  field: CustomerSortField,
): CustomerSort => ({
  direction:
    current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
  field,
});

const SORT_FIELD_TO_ROW_VALUE: Record<
  CustomerSortField,
  (row: FinanceHubCustomerRow) => boolean | number | string
> = {
  accountId: (row) => row.accountIdLabel,
  autopay: (row) => row.hasAutopay,
  contactInfo: (row) => row.hasContactInfo,
  customerName: (row) => row.customerNameLabel,
  invoiceCount: (row) => row.invoiceCount,
  outstanding: (row) => row.outstandingInMicros,
  paymentMethod: (row) => row.hasPaymentMethod,
  paymentPlan: (row) => row.hasPaymentPlan,
  statement: (row) => row.hasStatement,
};

const compareValues = (
  left: boolean | number | string,
  right: boolean | number | string,
): number => {
  if (typeof left === 'string' && typeof right === 'string') {
    return left.localeCompare(right);
  }

  return Number(left) - Number(right);
};

/**
 * Sorts customer rows client-side by the selected column.
 *
 * Ties fall back to customer name then chat id, matching the order the API
 * returns rows in, so equal values keep a stable position between renders.
 *
 * @param rows - The rows to sort
 * @param sort - The active sort selection
 *
 * @returns A new sorted array; the input is left untouched
 */
export const sortCustomerRows = (
  rows: FinanceHubCustomerRow[],
  sort: CustomerSort,
): FinanceHubCustomerRow[] => {
  const getValue = SORT_FIELD_TO_ROW_VALUE[sort.field];
  const directionFactor = sort.direction === 'asc' ? 1 : -1;

  return [...rows].sort((left, right) => {
    const byField = compareValues(getValue(left), getValue(right));
    if (byField !== 0) {
      return byField * directionFactor;
    }

    const byName = left.customerNameLabel.localeCompare(
      right.customerNameLabel,
    );
    if (byName !== 0) {
      return byName;
    }

    return left.chatId.localeCompare(right.chatId);
  });
};
